import { useCallback, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import U2000SaveCancelDialog from '../../../../u2000Core/components/dialog/U2000SaveCancelDialog';
import U2000TagAutocomplete from '../../../../u2000Core/components/tables/commons/search/U2000TagAutocomplete';
import type { U2000CriteriaTag } from '../../../../u2000Core/components/tables/commons/search/u2000CriteriaTags';
import { NS } from '../../../U3206';
import {
  u3206FilterMissionsForAutocomplete,
  type U3206GroupMissionLink,
  type U3206MissionRef,
} from '../../../U3206Service';

interface U3206MissionsAddDlgProps {
  isOpen: boolean;
  onClose: (action: 'save' | 'cancel') => void;
  onConfirm: (links: U3206GroupMissionLink[]) => void;
  catalog: U3206MissionRef[];
  linkedMissionIds: string[];
  t: TFunction;
}

function U3206MissionsAddDlg({
  isOpen,
  onClose,
  onConfirm,
  catalog,
  linkedMissionIds,
  t,
}: U3206MissionsAddDlgProps) {
  const [tags, setTags] = useState<U2000CriteriaTag[]>([]);

  /** Missions déjà sur le groupe + déjà choisies dans ce dialog. */
  const excludedMissionIds = useMemo(
    () => [...linkedMissionIds, ...tags.map((tag) => tag.value).filter(Boolean)],
    [linkedMissionIds, tags],
  );

  const handleClose = useCallback(
    (action: 'save' | 'cancel') => {
      if (action === 'save') {
        const links = tags
          .map((tag) => {
            const meta = tag.meta as U3206MissionRef | undefined;
            if (!meta?.id) return null;
            return {
              missionId: meta.id,
              code: meta.code,
              title: meta.title,
            } satisfies U3206GroupMissionLink;
          })
          .filter((link) => link != null) as U3206GroupMissionLink[];
        onConfirm(links);
      }
      setTags([]);
      onClose(action);
    },
    [onClose, onConfirm, tags],
  );

  if (!isOpen) return null;

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t('dlgAddMissionsTitle', { ns: NS })}
      width="large"
      isValid={tags.length > 0}
      contentOverflowVisible
    >
      <div className="flex flex-col gap-y-2">
        <label className="text-sm font-medium text-gray-700">{t('lblSelectMissions', { ns: NS })}</label>
        <U2000TagAutocomplete<U3206MissionRef>
          value={tags}
          onChange={setTags}
          allowFreeText={false}
          minSearchLength={0}
          searchCriterionField="code"
          placeholder={t('placeholderMissionTags', { ns: NS })}
          searchingLabel={t('lblSearching', { ns: NS })}
          noResultsLabel={(query) => t('lblNoMissionResults', { ns: NS, query })}
          onSearch={async (criterias) => {
            const query = String(criterias.find((c) => c.fieldName === 'code')?.value ?? '');
            return u3206FilterMissionsForAutocomplete(catalog, excludedMissionIds, query);
          }}
          itemToTag={(mission) => ({
            value: mission.id,
            label: `${mission.code} — ${mission.title}`,
            meta: mission,
          })}
          getTagKey={(tag) => tag.value}
          getTagLabel={(tag) => tag.label ?? tag.value}
          renderSuggestion={(mission) => (
            <>
              <span className="font-semibold">{mission.code}</span>
              <span className="ml-2 text-gray-600">{mission.title}</span>
            </>
          )}
          removeTagAriaLabel={(tag) => t('ariaRemoveTag', { ns: NS, label: tag.label ?? tag.value })}
          footerText={t('lblMissionTagCount', { ns: NS, count: tags.length })}
        />
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3206MissionsAddDlg;
